import {SPRING_8} from '@gaia/colors';
import {useUser} from '@gaia/stores/user';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {trialStatusSnackbarZIndex} from '@gaia/utils/gaia/zIndices';
import {Button} from '@mui/material';
import {useEffect, useState} from 'react';

import {
    SidebarAwareCenteredSnackbar,
    SidebarAwareCenteredSnackbarProps,
} from './snackbars/SidebarAwareCenteredSnackbar';

const TRIAL_ID = '1WeekPremiumTrial';
// The number of days the snackbar should show post-trial
const SNACKBAR_EXPIRY = -7;

const getTimeRemaining = (subscription: SubscriptionPlanResponse | undefined) => {
    // If the user has no current or previous subscription, they are most likely eligible for a trial.
    // Defaulting to 7 ensures that the snackbar doesn't show "Trial expired" to a user who has just started a trial.
    if (!subscription) return {hoursRemaining: 7 * 24, daysRemaining: 7};
    const {endDate} = subscription;
    const todayDate = new Date();
    // The server timestamp omits the timezone offset, so we have to manually account for it
    const offsetInMillis = new Date().getTimezoneOffset() * 60 * 1000;
    const hoursRemaining =
        ((new Date(endDate) as any) - (todayDate as any) - offsetInMillis) / 1000 / 60 / 60;
    const daysRemaining = Math.ceil(hoursRemaining / 24);
    return {hoursRemaining, daysRemaining};
};

const getTrialMessage = (daysRemaining: number) => {
    if (daysRemaining === 1) {
        return '1 day remaining in your trial';
    } else if (daysRemaining > 0) {
        return `${daysRemaining} days remaining in your trial`;
    } else {
        return 'Your trial has expired';
    }
};

type SubscriptionPlanResponse = {
    appname: string;
    autorenewing: boolean;
    canceled: boolean;
    created: string; // "2022-04-12"
    dataset: string;
    desc: string;
    endDate: string; // "2022-04-19 22:21:01"
    identifier: string;
    name: string;
    provider: string;
    startDate: string; // "2022-04-12 22:21:01.783909"
    trial: boolean;
};

export default function TrialStatusSnackbar({
    mapControlsOffset,
    open: canOpen,
    shouldShowErrorIfExpired,
}: {shouldShowErrorIfExpired: boolean} & SidebarAwareCenteredSnackbarProps): JSX.Element {
    const {id: userId, isEligibleForTrial} = useUser();
    const [subscription, setSubscription] = useState<SubscriptionPlanResponse | undefined>();
    const [showIneligibilitySnackbar, setShowIneligibilitySnackbar] = useState<boolean>(false);

    const {hoursRemaining, daysRemaining} = getTimeRemaining(subscription);

    // Fetch subscriptions when user logs in and when trial eligibility changes
    useEffect(() => {
        async function fetchSubscriptions() {
            try {
                // The backend has a 60s cache on this endpoint; it's possible for a user
                // to activate a trial within 60 seconds of the first time this hook
                // runs so we explicitly disable the caching via the Cache-Control header.
                const response = await fetch('/user/subscriptionStatus/', {
                    headers: {
                        'Cache-Control': 'no-cache',
                    },
                    credentials: 'same-origin',
                }).then((resp) => resp.json());
                let subscriptionResponse;
                if (response.subscription) {
                    // The user has a current subscription
                    subscriptionResponse = response.subscription;
                } else if (response.last_subscription) {
                    // If a user has no current subscription, the endpoint will return last_subscription,
                    // which we use to display the snackbar for a period after the trial has expired.
                    subscriptionResponse = response.last_subscription;
                }
                if (subscriptionResponse?.identifier === TRIAL_ID) {
                    // Show the trial snackbar for users currently or previously in a trial
                    setSubscription(subscriptionResponse);
                } else if (shouldShowErrorIfExpired && !isEligibleForTrial) {
                    // User has a non-trial subscription, and is ineligible
                    setShowIneligibilitySnackbar(true);
                }
            } catch (e) {
                console.error('Failed to fetch user subscriptions', e);
            }
        }
        if (userId) {
            fetchSubscriptions().catch(console.error);
        }
    }, [isEligibleForTrial, shouldShowErrorIfExpired, userId]);

    return showIneligibilitySnackbar ? (
        <SidebarAwareCenteredSnackbar
            open={showIneligibilitySnackbar}
            sx={{zIndex: trialStatusSnackbarZIndex}}
            ContentProps={{style: {justifyContent: 'center'}}}
            onClose={() => setShowIneligibilitySnackbar(false)}
            mapControlsOffset={mapControlsOffset}
            message={'Your account is ineligible for a free trial.'}
            autoHideDuration={6000}
            key='trialResponse'
        />
    ) : (
        <SidebarAwareCenteredSnackbar
            sx={{zIndex: trialStatusSnackbarZIndex}}
            open={
                (Boolean(subscription && canOpen) && daysRemaining > SNACKBAR_EXPIRY) ||
                (shouldShowErrorIfExpired && daysRemaining < SNACKBAR_EXPIRY)
            }
            autoHideDuration={daysRemaining < SNACKBAR_EXPIRY ? 6000 : undefined}
            mapControlsOffset={mapControlsOffset}
            message={getTrialMessage(daysRemaining)}
            action={
                <Button
                    style={{color: SPRING_8}}
                    href='/checkout/premium/'
                    onClick={() =>
                        ampli.clickGetPremium({
                            context: 'Map View Trial Bar',
                            trialIdentifier: 'trial97',
                            trialTimeLeft: hoursRemaining,
                        })
                    }
                >
                    Get Premium
                </Button>
            }
            key='trialStatus'
        />
    );
}
